import { useState } from "react";
import {
  generateStoriesAI,
  createStoryDrafts,
  approveStory,
  rejectStory,
} from "@/lib/ai";
import { toast } from "sonner";
import { Sparkles, Check, X, Pencil } from "lucide-react";
import { Tag } from "@/components/Tag";

/**
 * Renders inline in the BeliefDetailPage. Provides:
 * - Generate story drafts button
 * - Review pane for current drafts (approve / reject)
 */
export function StoryGenerator({ belief, pendingStories, onChange }) {
  const [loading, setLoading] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await generateStoriesAI({
        belief_text: belief.belief_text,
        belief_category: belief.category,
      });
      if (!res.drafts || res.drafts.length === 0) {
        toast.error("AI returned no drafts. Try again.");
        return;
      }
      const saved = await createStoryDrafts(
        belief.id,
        res.drafts.map((d) => ({
          story_type: d.story_type,
          story_text: d.story_text,
          source: d.source || "",
        })),
      );
      toast.success(`Drafted ${saved.created} stories. Review them below.`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const approve = async (id) => {
    setReviewingId(id);
    try {
      await approveStory(id);
      toast.success("Story approved.");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setReviewingId(null);
    }
  };

  const reject = async (id) => {
    setReviewingId(id);
    try {
      await rejectStory(id);
      toast.success("Draft discarded.");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setReviewingId(null);
    }
  };

  return (
    <div data-testid="story-generator">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.1em] text-[#141B4D]/60 mb-1">
            AI Story Generator
          </div>
          <div className="text-sm text-[#141B4D]/70 max-w-xl">
            Draft a Non-fiction, Fictional, Metaphor, Quote, Song, Statistic
            and Meme story to support this belief. You approve each one before
            it counts toward filling the gap.
          </div>
        </div>
        <button
          onClick={generate}
          disabled={loading}
          data-testid="generate-stories-button"
          className="inline-flex items-center gap-2 bg-[#141B4D] text-[#F2E9DB] px-4 py-2 rounded-sm text-sm font-medium hover:bg-[#141B4D]/90 disabled:opacity-60"
        >
          <Sparkles size={14} strokeWidth={2.5} />
          {loading ? "Drafting…" : "Generate story drafts"}
        </button>
      </div>

      {pendingStories.length > 0 && (
        <div
          className="border border-[#FFD100] bg-[#FFD100]/10 rounded-sm p-5 mb-6"
          data-testid="pending-drafts-banner"
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-block w-2 h-2 rounded-full bg-[#141B4D] animate-pulse" />
            <span className="text-sm font-bold text-[#141B4D]">
              {pendingStories.length} draft{pendingStories.length === 1 ? "" : "s"} waiting for review
            </span>
          </div>
          <div className="space-y-3">
            {pendingStories.map((s) => (
              <div
                key={s.id}
                data-testid={`pending-draft-${s.id}`}
                className="bg-white border border-[#141B4D]/15 rounded-sm p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Tag>{s.story_type}</Tag>
                  {s.source && (
                    <span className="text-xs text-[#141B4D]/60">
                      Source: {s.source}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#141B4D] leading-relaxed whitespace-pre-wrap">
                  {s.story_text}
                </p>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#141B4D]/10">
                  <button
                    onClick={() => approve(s.id)}
                    disabled={reviewingId === s.id}
                    data-testid={`approve-draft-${s.id}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-[#141B4D] text-[#F2E9DB] hover:bg-[#141B4D]/90 disabled:opacity-60"
                  >
                    <Check size={12} /> Approve
                  </button>
                  <button
                    onClick={() => reject(s.id)}
                    disabled={reviewingId === s.id}
                    data-testid={`reject-draft-${s.id}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border border-[#141B4D]/20 text-[#141B4D] hover:bg-[#141B4D]/5 disabled:opacity-60"
                  >
                    <X size={12} /> Discard
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
